import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { getChangeHistoryByEmRequest } from 'redux/reducers/changeHistory/reducer';
import { useDispatch, useSelector } from 'react-redux';
import {
	getChangeHistoryByEm,
	getChangeHistoryByEmLoading,
} from 'redux/reducers/changeHistory/selectors';
import { useLocation, useParams } from 'react-router-dom';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import queryString from 'query-string';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { TFilters, TOrderBy } from 'redux/types';
import { DATES_ARRAY, DATES_ID } from 'services/constants/dates-constants';
import { TAuditLogItem } from 'redux/reducers/changeHistory/types';
import { tableStructure } from './tableStructure';
import { EMManagementIdHeader } from '../EMManagementIdHeader';
import { getSavedUrl2 } from '../../../../redux/reducers/auth/selectors';

export const ChangeHistory = () => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const changeHistory = useSelector(getChangeHistoryByEm);
	const changeHistoryLoading = useSelector(getChangeHistoryByEmLoading);
	const savedUrl2 = useSelector(getSavedUrl2);
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TAuditLogItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const { page } = queryString.parse(search);

	const filters: TFilters = currentTableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector as string] && item.type === ETableStructureType.DATE) {
			if (navigateParams[item.selector as string] === DATES_ID.CUSTOM_RANGE) {
				if (navigateParams[`${String(item.selector)}StartDate`]) {
					acc.push({
						fieldName: String(item.selector),
						value: navigateParams[`${String(item.selector)}StartDate`] as string,
						operator: '>=',
					});
				}
				if (navigateParams[`${String(item.selector)}EndDate`]) {
					acc.push({
						fieldName: String(item.selector),
						value: navigateParams[`${String(item.selector)}EndDate`] as string,
						operator: '<=',
					});
				}
				return acc;
			}
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector as string]);
			if (dates?.from)
				acc.push({ fieldName: String(item.selector), value: dates.from, operator: '>=' });
			if (dates?.to)
				acc.push({ fieldName: String(item.selector), value: dates.to, operator: '<=' });
			return acc;
		}
		if (navigateParams[item.selector as string])
			acc.push({
				fieldName: String(item.selector),
				value: navigateParams[item.selector as string] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: currentTableStructure[+navigateParams.sortIndex]?.selector as string,
				direction:
					navigateParams?.sortDirection && String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getChangeHistoryByEmRequest({
				id: Number(emId),
				body: {
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
					filters,
					orderBy,
				},
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search, emId]);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Box sx={{ mb: 2 }}>
				<EMManagementIdHeader savedUrl={savedUrl2} />
			</Box>
			<EasTableWithFilter
				tableStructure={currentTableStructure}
				items={changeHistory}
				itemsIsLoading={changeHistoryLoading}
				handleAction={() => {}}
				handleLink={() => {}}
				skeletonRows={[1, 1, 1, 1, 1, 1, 1, 1]}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Change History
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{changeHistoryLoading && <CircularProgress size={20} />}
						{/* <Button variant="contained" color="primary" size="small">
							Add History
						</Button> */}
					</Box>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
